const ENV_NAME = process.env.ENV_NAME || ""
const SLOT = process.env.SLOT || "production"
const ENV_TYPE = process.env.ENV_TYPE || "dev"
const VERSION = process.env.VERSION || "v0.0.0"
const IS_TEST = process.env.IS_TEST === "true"
const IS_REMOTE_AUTH = process.env.IS_REMOTE_AUTH === "true"
const IS_REMOTE_GRAPH = process.env.IS_REMOTE_GRAPH === "true"
const REGION = process.env.REGION || process.env.REGION_NAME || "local"

const config = {
  /**
   * When a deployment is created from a PR, the name of the deployment
   * is the same as the source branch
   */
  envName: ENV_NAME,

  /**
   * Which environment the app is running in. There are 4 types of environments:
   * - dev: running locally on a developer's machine
   * - prod: running either as a released or staged version remotely
   */
  envType: ENV_TYPE,
  /** Running on production (can be released or staged) */
  isProd: ENV_TYPE === "prod",
  /** Running locally on developer's computer */
  isDev: ENV_TYPE === "dev",

  /** Which deployment region is hosting the app (eastno, westeu or local) */
  region: REGION,
  /** Running locally on developer's computer */
  isLocal: REGION === "local",
  /** Running remotely in Azure */
  isRemote: REGION !== "local",
  /** Running on a failover region (westeu) */
  isFailover: REGION === "westeu",

  /** Running tests */
  isTest: IS_TEST,
  /** Running as a released deployment */
  isRelease: ENV_NAME === "release",
  /** Running as a staged deployment */
  isStaging: ENV_NAME === "staging",
  /**
   * Running remotely in the `stable` environment (the universal non-prod
   * fallback resource for other environments)
   */
  isStable: ENV_NAME === "stable",

  /**
   * Forces auth to be configured remotely. Useful when the app starts
   * locally, but only partially
   */
  isRemoteAuth: IS_REMOTE_AUTH,
  /**
   * Forces clients to use the remote supergraph. Useful when the app starts
   * locally, but only partially
   */
  isRemoteGraph: IS_REMOTE_GRAPH,

  /**
   * Azure App Service can have multiple deployment slots, which are essentially copies
   * of the app running on the same underlying VM, but as different container. Currently
   * only the production resources are configured with multiple slot, one for release
   * called "production" and one for staging called "staging". All other resources have
   * only one slot called "production" - this is a bit confusing and should not be compared
   * with a production release of the application
   */
  slot: SLOT,
  /** dev/pre-releases will be postfixed with a short commit SHA */
  version: VERSION,

  /** used to adjust the granularity of logging calls */
  logLevel: "info",

  /**
   * Used for tracking the deployment with the following format:
   * `{config.envName}@{config.envType}-{config.version}-{config.region}-{config.slot}`
   */
  envHeader: "",

  /** The app will send performance metrics (ie. sentry tracing) */
  isInstrumentationEnabled: false,
  /** The app will send usage tracking (ie. segment/mixpanel) */
  isTrackingEnabled: false,

  /** Source is Hubspot. Determines if the CurrentUser has admin access */
  placepointExternalCustomerId: "6894908674",

  /**
   * Which identity platform the console authenticates against (FUS-927):
   * - "entra": Microsoft Entra External ID (placepointdev on dev/preview,
   *   placepointprod on prod) — native
   *   authentication renders login/signup/reset in-app; no hosted pages,
   *   no B2C policies.
   * - "b2c": legacy Azure AD B2C — hosted user-flow pages via loginRedirect.
   * Every environment runs "entra", prod included since the FUS-927 cutover
   * on 2026-08-31; "b2c" survives only as the kill-switch fallback in the
   * isProd override below.
   */
  azAuthIdp: "entra",
  azAuthLoginDomain: "placepointdev.ciamlogin.com",
  // B2C user-flow policy names — only meaningful while azAuthIdp === "b2c"
  // (the kill-switch fallback). External ID has no per-policy authority segment.
  azAuthRegisterPolicyName: "B2C_1_register_v2",
  azAuthLoginPolicyName: "B2C_1_login_v2",
}

if (config.isRemote) {
  config.isInstrumentationEnabled = true
}

/**
 * placepointprod External ID coordinates (FUS-927 prod bootstrap), from
 * `provisionTenant.sh` run against the placepointprod tenant on
 * 2026-08-13 (tools/entra/ACTIVATION.md). The empty-tenantId guard below
 * remains as the kill switch: blanking tenantId reverts the staging tier
 * to B2C on the next build. Consumed by v2-console-shared/config.ts for
 * the matching tenant/client id override — single source, never
 * re-declare.
 */
export const PROD_ENTRA = {
  tenantId: "db862da0-eaab-41e5-8b86-48fd893b8d63",
  clientId: "830fd14c-2519-4aab-bbd2-a834da889171",
  loginDomain: "placepointprod.ciamlogin.com",
}

if (config.isProd) {
  config.isTrackingEnabled = true
  config.azAuthIdp = "b2c"
  config.azAuthLoginDomain = "login.placepoint.no"
  // FUS-927 cutover (31 Aug 2026): EVERY prod tier — release included —
  // rides the Entra External ID tenant now that it is live. B2C stays only
  // as the token issuer of pre-migration sessions (backends accept both
  // issuers); new sign-ins all go through Entra native auth. The b2c
  // values above remain as the paper-trail fallback while PROD_ENTRA is
  // filled — emptying PROD_ENTRA.tenantId reverts prod to B2C in one line.
  if (PROD_ENTRA.tenantId !== "") {
    config.azAuthIdp = "entra"
    config.azAuthLoginDomain = PROD_ENTRA.loginDomain
  }
}

if (config.isStaging) {
  // Only meaningful while a tier still runs B2C flows; harmless on Entra.
  config.azAuthRegisterPolicyName = "B2C_1_register_v2_staging"
}

config.envHeader = `${config.envName}@${config.version}-${config.region}-${config.slot}`

export default config
